/* eslint-disable react/prop-types */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState } from "react";
import { Trash2, Edit, Eye } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "react-hot-toast";
import { DeleteConfirmDialog } from "../../dialog/staff-manage/all-staff-dialog";
import { Link } from "react-router-dom";
import { InfoDialog } from "@/components/dialog/info-dialog";
import { toggleDiscountApi } from "@/api/settings/discount";
import hasPermission from "@/hooks/hasPermission";

export function DiscountManagementTable({
  setTableData,
  tableData,
  deleteDiscount,
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [availableDaysDialog, setAvailableDaysDialog] = useState(false);
  const [exceptDialog, setExceptDialog] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [personToDelete, setPersonToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState(null);

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedDiscount(null);
  };

  const handleToggleDiscountStatus = (discountId, checked) => {
    setToggleLoadingId(discountId);
    const newStatus = checked ? "active" : "inactive";

    toggleDiscountApi(discountId, newStatus)
      .then((data) => {
        if (data.status === "success") {
          const updatedData = tableData.map((d) =>
            d.id === discountId
              ? {
                  ...d,
                  status: newStatus,
                }
              : d,
          );
          setTableData(updatedData);
        } else {
          toast.error("Failed to update discount status");
        }
      })
      .catch(() => {
        toast.error("Failed to update discount status");
      })
      .finally(() => {
        setToggleLoadingId(null);
      });
  };

  return (
    <div className="w-full overflow-x-auto">
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead>Discount Name</TableHead>
            <TableHead>Order Type</TableHead>
            <TableHead>Available Days</TableHead>
            <TableHead>Except Item </TableHead>
            <TableHead>Conjunct With</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Minimum Order</TableHead>
            <TableHead>Pay Method</TableHead>
            <TableHead>Customer Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableData?.length == 0 && (
            <TableRow>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell colSpan="8" className="text-center">
                No discount found
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          )}
          {tableData?.length > 0 &&
            tableData?.map((discount) => (
              <TableRow key={discount?.id}>
                <TableCell>{discount?.name}</TableCell>
                <TableCell>
                  {discount?.order_type === "pickup" && "Pickup"}
                  {discount?.order_type === "in_house" && "In House"}
                  {discount?.order_type === "table" && "Table"}
                  {discount?.order_type === "delivery" && "Delivery"}
                </TableCell>
                <TableCell>
                  <Eye
                    size={18}
                    className="cursor-pointer"
                    onClick={() => {
                      setAvailableDaysDialog(true);
                      setSelectedDiscount(discount);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Eye
                    size={18}
                    className="cursor-pointer"
                    onClick={() => {
                      setExceptDialog(true);
                      setSelectedDiscount(discount);
                    }}
                  />
                </TableCell>
                <TableCell>
                  {discount?.not_conjunct_with_other ? "Yes" : "No"}
                </TableCell>
                <TableCell>
                  {discount?.discount_type === "percentage"
                    ? discount?.discount_value + "%"
                    : "£" + discount?.discount_value}
                </TableCell>
                <TableCell>{discount?.minimum_order}</TableCell>
                <TableCell>
                  {discount?.payment_method == "cash" && "Cash"}
                  {discount?.payment_method == "card" && "Card"}
                  {discount?.payment_method == "cash_or_card" && "Cash & Card"}
                </TableCell>
                <TableCell>{discount?.customer_type?.name}</TableCell>
                <TableCell>
                  <Switch
                    size="sm"
                    checked={discount?.status === "active"}
                    disabled={toggleLoadingId === discount?.id}
                    onCheckedChange={(checked) => {
                      handleToggleDiscountStatus(discount?.id, checked);
                    }}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {hasPermission("back_office.settings.discount_update") && (
                      <Link
                        to={`/backoffice/settings/discount/edit/${discount?.id}`}
                        state={discount}
                      >
                        <button className="text-color7">
                          <Edit size={18} />
                        </button>
                      </Link>
                    )}
                    {hasPermission("back_office.settings.discount_delete") && (
                      <button
                        onClick={() => {
                          setIsDialogOpen(true);
                          setSelectedDiscount(discount);
                        }}
                        className="text-color8"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <DeleteConfirmDialog
        isLoading={loading}
        title="Delete Discount"
        description="Are you sure you want to delete this discount? This action cannot be undone."
        yesBtnTxt="Delete"
        noBtnTxt="Cancel"
        isOpen={isDialogOpen}
        onClose={closeDialog}
        onConfirm={() => {
          deleteDiscount(selectedDiscount?.id);
        }}
        personName={personToDelete || ""}
      />
      <InfoDialog
        isOpen={availableDaysDialog}
        onClose={() => {
          setAvailableDaysDialog(false);
          setSelectedDiscount(null);
        }}
        title="Available Days"
        button={false}
      >
        {selectedDiscount &&
          selectedDiscount?.days &&
          selectedDiscount?.days?.length > 0 && (
            <div className=" p-5">
              <div className=" flex flex-col gap-2">
                {selectedDiscount?.days?.map((day, index) => (
                  <div key={index} className=" flex gap-1">
                    <div className=" w-[75px] font-semibold text-cyellow">
                      {day?.name}:{" "}
                    </div>
                    <span className=""> {day?.start_time} </span>
                    <span> - </span>
                    <span> {day?.end_time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
      </InfoDialog>
      <InfoDialog
        isOpen={exceptDialog}
        onClose={() => {
          setExceptDialog(false);
          setSelectedDiscount(null);
        }}
        title="Except Items"
        button={false}
      >
        {selectedDiscount &&
          selectedDiscount?.except_items &&
          selectedDiscount?.except_items?.length > 0 && (
            <div className=" p-5">
              <div className=" w-full flex flex-wrap gap-1">
                {selectedDiscount?.except_items?.map((item, index) => (
                  <div
                    key={index}
                    className=" grow bg-color6 text-color1 rounded-md px-2 text-center "
                  >
                    {item?.name}{" "}
                  </div>
                ))}
              </div>
            </div>
          )}
      </InfoDialog>
    </div>
  );
}
